import React from 'react'

import Button from 'components/Button'
import T from 'components/FormattedMessage'
import CenteredContent from 'components/CenteredContent'
import GameCreationScreen from 'components/GameCreationScreen'

import { Snackbar, Divider } from '@material-ui/core'

import { GamePrizeInfoResponse, MatchDetailsResponse, CreateGameResponse, GameType } from 'protos'
import { CreateGameErrorToken } from 'lib/enumTranslations'
import { RouteComponentProps } from 'react-router'
import { Action } from 'types'
import config from 'config'

import helpIconUrl from 'assets/images/coins_icon.svg'

import SelectStakeAutoSub from './components/SelectStakeAutoSub'
import SelectStake from './components/SelectStake'

interface Props {
  getGamePrizeInfo: () => Promise<GamePrizeInfoResponse>
  getMatchDetails: () => Promise<MatchDetailsResponse>
  onEntryFeeSelect: (entryFee: number, autosub: boolean) => Promise<CreateGameResponse>
  balance: number
  gameType: GameType
}

type State = {
  gamePrizeInfo: Action<GamePrizeInfoResponse | null>
  matchDetails: Action<MatchDetailsResponse | null>
  createGame: Action<CreateGameResponse | null>
  autosub: boolean
  selectedFeeIndex: number
}

type InjectedProps = Props & RouteComponentProps<{}>

class SelectStakeScreen extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)

    this.state = {
      gamePrizeInfo: { isLoading: true },
      matchDetails: { isLoading: true },
      createGame: { isLoading: false },
      selectedFeeIndex: 0,
      autosub: false,
    }
  }

  componentDidMount() {
    this.props.getGamePrizeInfo()
      .then((result: GamePrizeInfoResponse) => {
        this.setState({ gamePrizeInfo: { isLoading: false, result }, selectedFeeIndex: result.defaultFeeOptionIndex })
      })
      .catch((err: Error) => {
        this.setState({ gamePrizeInfo: { isLoading: false, errorCode: err.message } })
      })

    this.props.getMatchDetails()
      .then((result: MatchDetailsResponse) => {
        this.setState({ matchDetails: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ matchDetails: { isLoading: false, errorCode: err.message } })
      })
  }

  onGameCreate = () => {
    this.setState({ createGame: { isLoading: true } })

    this.props.onEntryFeeSelect(this.getSelectedEntryFee(), this.state.autosub)
      .then((result: CreateGameResponse) => {
        if (result.result !== CreateGameResponse.Result.CGR_SUCCESS) {
          throw new Error(CreateGameErrorToken(result.result))
        }

        this.setState({ createGame: { isLoading: false, result } })

        this.props.history.push('/games')
      })
      .catch((err: Error) => {
        this.setState({ createGame: { isLoading: false, errorCode: err.message } })
      })
  }

  renderSubmitButton = () => {
    const { gameType } = this.props
    const { createGame, gamePrizeInfo } = this.state

    if (!gamePrizeInfo.result) {
      return null
    }

    const hasEnoughBalance = this.isBalanceSufficient()

    if (config.IS_STORE_DISABLED || hasEnoughBalance) {
      return (
        <Button
          rounded={false}
          fullWidth
          size="large"
          disabled={createGame.isLoading || !hasEnoughBalance}
          color="primary"
          variant="contained"
          onClick={this.onGameCreate}
        >
          <T id={gameType === GameType.GT_PRIVATE ? 'game_action_acceptprivate' : 'game_action_acceptpublic'} />
        </Button>
      )
    }

    return (
      <Button
        rounded={false}
        fullWidth
        to="/store"
        size="large"
        variant="contained"
        color="dangerous"
      >
        <T id="game_action_buymorekoins" />
      </Button>
    )
  }

  getFeeOptions = () => {
    return this.state.gamePrizeInfo?.result?.feeOption ?? []
  }

  getSelectedEntryFee = () => {
    const selectedFeeOption = this.getFeeOptions()[this.state.selectedFeeIndex]
    const selectedEntryFee = Number(selectedFeeOption.entryFee!.value)

    return selectedEntryFee
  }

  isBalanceSufficient = () => {
    return this.props.balance >= this.getSelectedEntryFee()
  }

  clearError = () => {
    this.setState(state => ({ createGame: { ...state.createGame, errorCode: undefined } }))
  }

  renderContent = () => {
    const { matchDetails, createGame } = this.state
    const { balance } = this.props

    if (!matchDetails.result || !matchDetails.result.match) {
      return null
    }

    return (
      <CenteredContent>
        <SelectStake
          balance={balance}
          feeOptions={this.getFeeOptions()}
          isBalanceSufficient={this.isBalanceSufficient()}
          selectedMatch={matchDetails.result.match}
          selectedFeeIndex={this.state.selectedFeeIndex}
          setSelectedFeeIndex={(index) => { this.setState({ selectedFeeIndex: index }) }}
        />
        {!config.IS_STORE_DISABLED && <Divider />}
        <SelectStakeAutoSub
          isActivated={this.state.autosub}
          onToggle={() => { this.setState(state => ({ autosub: !state.autosub })) }}
        />
        {!config.IS_STORE_DISABLED && <Divider />}
        <Snackbar
          open={!!createGame.errorCode}
          message={createGame.errorCode && <T id={createGame.errorCode} />}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={this.clearError}
        />
      </CenteredContent>
    )
  }

  render() {
    const { matchDetails, gamePrizeInfo } = this.state
    const { history } = this.props

    return (
      <GameCreationScreen
        title={<T id="gameconfirmation_title" />}
        isLoading={matchDetails.isLoading || gamePrizeInfo.isLoading}
        errorCode={matchDetails.errorCode || gamePrizeInfo.errorCode}
        component={this.renderContent}
        onBack={history.goBack}
        bottomChild={this.renderSubmitButton()}
        help={{
          iconUrl: helpIconUrl,
          title: 'gameconfirmation_help_title',
          message: 'gameconfirmation_help_text',
          buttonLabel: 'gameconfirmation_help_dismiss',
        }}
      />
    )
  }
}

export default SelectStakeScreen
